import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@mui/styles";
import { Box, Paper } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { FormattedMessage, useIntl } from "react-intl";
import { useMutation } from "@apollo/client";
import { useAlert } from "react-alert";

import { useSnackbar } from "../../hooks";
import logger from "../../helpers/logger";
import { REMOVE_SITE_USER, UPDATE_SITE_USER } from "../../graphql/mutations";
import { UPDATE_SITEUSER_PAYMENTLIABILITY } from "../../graphql/mutations/SiteUsers";
import { useGetSiteUsers } from "../../hooks";
import { useGetSiteRoles } from "../../hooks/useGetSiteRoles";
import { updateSiteUser } from "../../actions";

import SiteUsersTable from "../../components/SiteUsers/SiteUsersTable";
import ManageRolesModal from "../../components/SiteUsers/ManageRolesModal";
import SiteUserHeader from "./SiteUsersHeader";
import SiteSelectModal from "../../components/common/SiteSelectModal";
import InviteUsersModals from "../../components/SiteUsers/InviteUserModals";
import { FILTER_NAMES } from "../../components/SiteUsers/SiteUsersTable/filters";
import PaymentLiabilityModal from "../../components/SiteUsers/PaymentLiabilityModal";

const { EMAIL } = FILTER_NAMES;

const useStyles = makeStyles(theme => ({
  root: {
    flexGrow: 1,
    padding: theme.spacing(3),
  },
  paper: {
    boxShadow: "0 6px 10px -4px rgba(0, 0, 0, 0.15)",
    borderRadius: 3,
  },
  paperBody: {
    padding: theme.spacing(4),
  },
}));

const SiteUsers = () => {
  const classes = useStyles();
  const intl = useIntl();
  const alert = useAlert();
  const snackbar = useSnackbar();
  const dispatch = useDispatch();

  const selectedWorkspace = useSelector(
    state => state.ui.toggleWorkspaces.workspace,
  );

  const siteUsers = useSelector(state => state.api.siteUsers.items);
  const siteUsersLoading = useSelector(state => state.api.siteUsers.loading);
  const sites = useSelector(state => state.api.currentViewer.viewer.sites);
  const siteRoles = useSelector(state => state.api.siteUsers.siteRoles);

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedSite, setSelectedSite] = useState(
    selectedWorkspace?.id || null,
  );
  const [selectedWorkspaceName, setSelectedWorkspaceName] = useState(
    selectedWorkspace?.name || null,
  );
  const [filter, setFilter] = useState("SHOW_ALL");
  const [sortfield, setSortfield] = useState(EMAIL);
  const [ascending, setAscending] = useState(true);
  const [siteRolesRights, setSiteRolesRights] = useState({});
  const [siteSelect, setSiteSelect] = useState(false);
  const [openManageRoles, setOpenManageRoles] = useState(false);
  const [openInviteModals, setOpenInviteModals] = useState(false);

  const filterOptions = [
    {
      label: intl.formatMessage({ id: "siteUsers.showAllUsers" }),
      value: "SHOW_ALL",
    },
    {
      label: intl.formatMessage({ id: "siteUsers.showInvitedUsers" }),
      value: "SHOW_INVITED",
    },
    {
      label: intl.formatMessage({ id: "siteUsers.showActiveUsers" }),
      value: "SHOW_ACTIVE",
    },
    {
      label: intl.formatMessage({ id: "siteUsers.showInactiveUsers" }),
      value: "SHOW_INACTIVE",
    },
  ];

  const { execute: getSiteUsers, loading: usersLoading } = useGetSiteUsers({
    ascending,
    filter,
    searchTerm,
    siteId: selectedSite,
    sortfield,
  });

  const { execute: getSiteRoles } = useGetSiteRoles({
    siteId: selectedSite,
  });

  useEffect(() => {
    if (!selectedSite) return;

    const found = sites.find(site => site.id === selectedSite);
    if (found?.siteRoleRights) {
      setSiteRolesRights(found.siteRoleRights);
    }

    getSiteRoles();
  }, [selectedSite]);

  useEffect(() => {
    if (selectedSite) {
      getSiteUsers();
    }
  }, [selectedSite, filter, searchTerm, sortfield, ascending]);

  const handleOpenManageRoles = () => {
    const canManageSiteRoles = Boolean(
      siteRolesRights?.canManageSiteRoles,
    );

    if (selectedSite && canManageSiteRoles) {
      setOpenManageRoles(true);
    } else {
      setOpenManageRoles(false);
    }
  };

  const handleSiteChanged = site => {
    const { siteId } = site;

    if (siteId !== 0) {
      setSelectedSite(siteId);
      setSelectedWorkspaceName(site.name);

      const [matchedSite] = sites.filter(s => s.id === siteId);
      setSiteRolesRights(matchedSite?.siteRoleRights || {});
    }
  };

  const siteRoleOptions = siteRoles.map(role => ({
    label: role.name,
    value: role.siteRoleId,
  }));

  const handleOpenInviteUser = () => setOpenInviteModals(true);
  const handleCloseInviteUser = () => setOpenInviteModals(false);

  const [removeSiteUser] = useMutation(REMOVE_SITE_USER);
  const [updateSiteUserMutation] = useMutation(UPDATE_SITE_USER);
  const [updatePaymentLiability] = useMutation(
    UPDATE_SITEUSER_PAYMENTLIABILITY,
  );

  const handleRemoveSiteUser = async (siteId, id) => {
    try {
      const res = await removeSiteUser({ variables: { siteId, id } });

      if (res?.data?.removeSiteUser?.success) {
        getSiteUsers();
        alert.success(
          <FormattedMessage id="siteUsers.removedUser" />,
        );
      } else {
        snackbar.workspaceError(
          <FormattedMessage id="siteUsers.errorRemoveUser" />,
        );
      }
    } catch (err) {
      snackbar.workspaceError(
        <FormattedMessage id="siteUsers.errorRemoveUser" />,
      );
      logger.error(
        `Error in removing site user. Unable to send ${err}`,
      );
    }
  };

  const [paymentLiabilityUser, setPaymentLiabilityUser] = useState();

  const handleOpenPaymentLiabiltyModal = (siteUser) => {
    setPaymentLiabilityUser(siteUser);
  }

  const handleUpdatePaymentLiability = async (
    user,
    editCompanyId,
    subscriptionLevel,
  ) => {
    try {
      const res = await updatePaymentLiability({
        variables: {
          input: {
            id: user.id,
            siteId: user.siteId,            
            editingCompanyId: editCompanyId?.companyId,
            subscriptionLevel,
          },
        },
      });

      if (
        res?.data?.siteUserContext?.updateSiteUserPaymentLiability
          ?.success
      ) {
        const siteUser =
          res.data.siteUserContext.updateSiteUserPaymentLiability
            .siteUser;
        dispatch(updateSiteUser(siteUser));
      } else {
        snackbar.workspaceError(
          <FormattedMessage id="siteUsers.errorUpdate" />,
        );
      }

      setPaymentLiabilityUser(null);
    } catch (err) {
      snackbar.workspaceError(
        <FormattedMessage id="siteUsers.errorUpdate" />,
      );
      logger.error(
        `Error in updating site user. Unable to send ${err}`,
      );
    }
  };

  const handleUpdateSiteUser = async params => {
    try {
      const res = await updateSiteUserMutation({
        variables: { siteUser: params },
      });

      if (res?.data?.updateSiteUser?.success) {
        alert.success(<FormattedMessage id="siteUsers.update" />);
        dispatch(updateSiteUser(res.data.updateSiteUser.siteUser));
      } else {
        snackbar.workspaceError(
          <FormattedMessage id="siteUsers.errorUpdate" />,
        );
      }
    } catch (err) {
      snackbar.workspaceError(
        <FormattedMessage id="siteUsers.errorUpdate" />,
      );
      logger.error(
        `Error in updating site user. Unable to send ${err}`,
      );
    }
  };

  return (
    <div className={classes.root}>
      <Paper className={classes.paper} elevation={0}>
        <SiteUserHeader
          filter={filter}
          filterOptions={filterOptions}
          setFilter={setFilter}
          handleOpenInviteUser={handleOpenInviteUser}
          handleOpenManageRoles={handleOpenManageRoles}
          loadingUsers={usersLoading}
          onSearchUsers={setSearchTerm}
          onSiteChanged={handleSiteChanged}
          selectedWorkspaceName={selectedWorkspaceName}
          siteRolesRights={siteRolesRights}
        />

        <Box className={classes.paperBody}>
          <SiteUsersTable
            fetchSiteUsers={getSiteUsers}
            siteRoleOptions={siteRoleOptions}
            sortfield={sortfield}
            ascending={ascending}
            setSortfield={setSortfield}
            setAscending={setAscending}
            siteRolesRights={siteRolesRights}
            siteUsers={siteUsers}
            siteUsersLoading={siteUsersLoading}
            onRemoveSiteUser={handleRemoveSiteUser}
            onUpdateSiteUser={handleUpdateSiteUser}
            onOpenPaymentLiabilityModal={handleOpenPaymentLiabiltyModal}
            onUpdateSiteUserPayment={handleUpdatePaymentLiability}
          />
        </Box>
      </Paper>

      {siteSelect && (
        <SiteSelectModal open={siteSelect} setOpen={setSiteSelect} />
      )}

      <InviteUsersModals
        open={openInviteModals}
        onCloseDialog={handleCloseInviteUser}
        selectedSite={selectedSite}
        siteRoleOptions={siteRoleOptions}
        getSiteUsers={getSiteUsers}
        setSiteSelect={setSiteSelect}
      />

      <PaymentLiabilityModal user={paymentLiabilityUser} onClose={() => setPaymentLiabilityUser()} onSave={handleUpdatePaymentLiability} />

      {openManageRoles && (
        <ManageRolesModal
          open={openManageRoles}
          selectedSite={selectedSite}
          selectedWorkspaceName={selectedWorkspaceName}
          setOpenManageRoles={setOpenManageRoles}
          siteRoles={siteRoles}
          siteRoleOptions={siteRoleOptions}
        />
      )}
    </div>
  );
};

export default SiteUsers;